import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MapPin, Calendar, Trophy, Zap, Gamepad2, ListOrdered, Loader2 } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import { formatPoints } from '../utils/points-label'
import { getPublicProfile, getPublicResults } from '../services/publicService'
import type { LeaderboardEntry, PublicPlayerPredictionItem, PublicProfile } from '../types/public'
import { getMatchDate, formatDate } from '../utils/dateHelper'
import MatchTeamsInline from './MatchTeamsInline'

interface PublicProfileModalProps {
  entry: LeaderboardEntry | null
  tournamentIds: string[]
  isOpen: boolean
  onClose: () => void
}

/** Winner / score split (room-style breakdown). Prefers backend point fields, else mirrors the public scoring rules. */
function computePointsSplit(item: PublicPlayerPredictionItem): { winnerPoint: number; scorePoint: number } {
  const { prediction, match } = item
  if (prediction.predictedWinnerPoint != null || prediction.predictedScorePoint != null) {
    return {
      winnerPoint: Number(prediction.predictedWinnerPoint) || 0,
      scorePoint: Number(prediction.predictedScorePoint) || 0,
    }
  }
  if (!match?.score || match.winner === undefined) return { winnerPoint: 0, scorePoint: 0 }
  const predictedWinner = prediction.winner ? String(prediction.winner).trim() : null
  const actualWinner = match.winner ? String(match.winner).trim() : null
  const winnerCorrect = predictedWinner === actualWinner
  let winnerPoint = 0
  if (winnerCorrect) winnerPoint = match.winner ? 1 : 2
  const predScoreStr = String(prediction.predictedScore || '').trim()
  const matchScoreStr = String(match.score || '').trim()
  let scorePoint = 0
  if (predScoreStr && matchScoreStr && predScoreStr === matchScoreStr && winnerCorrect) {
    const rules: Record<string, number> = {
      '1-0': 1, '2-0': 1, '2-1': 1, '0-0': 2, '1-1': 2, '2-2': 2,
      '3-0': 2, '3-1': 2, '3-2': 2, '3-3': 3, '4-0': 3, '4-1': 3, '4-2': 3, '4-3': 3, '4-4': 4,
    }
    scorePoint = rules[predScoreStr] ?? 1
  }
  return { winnerPoint, scorePoint }
}

function buildWinnerSuffix(winner: string | null | undefined, t: (k: string, o?: any) => string): string {
  const w = winner ? String(winner).trim() : ''
  if (!w || w === 'null') return ` (${t('stats.draw')})`
  return ` (${t('stats.teamWins', { team: w })})`
}

const PublicProfileModal: React.FC<PublicProfileModalProps> = ({
  entry,
  tournamentIds,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation()
  const participantId = entry?.publicParticipantId ?? ''
  const idsKey = tournamentIds.join(',')

  const { data: profile = null, isLoading: loadingProfile } = useQuery({
    queryKey: ['public-profile', participantId],
    queryFn: () => getPublicProfile(participantId),
    enabled: isOpen && !!participantId,
    staleTime: 60 * 1000,
  })

  const { data: results = [], isLoading: loadingResults } = useQuery({
    queryKey: ['public-results', participantId, idsKey],
    queryFn: () => getPublicResults(participantId, tournamentIds),
    enabled: isOpen && !!participantId && tournamentIds.length > 0,
    staleTime: 60 * 1000,
  })

  const loading = loadingProfile || loadingResults

  const sortedResults = useMemo(() => {
    return [...results].sort((a, b) => {
      const dateA = new Date(getMatchDate(a.match, true)).getTime()
      const dateB = new Date(getMatchDate(b.match, true)).getTime()
      return dateB - dateA
    })
  }, [results])

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  const displayName = profile?.userName ?? entry?.userName ?? '–'
  const joinedAtFormatted = profile?.joinedAt
    ? formatDate(profile.joinedAt, false)
    : null
  const locationText = [profile?.countryName ?? entry?.countryName, profile?.city ?? entry?.city]
    .filter(Boolean)
    .join(' · ') || null

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-2xl mx-2 sm:mx-4 glass-strong rounded-2xl sm:rounded-3xl overflow-hidden flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with avatar and close */}
        <div className="p-4 sm:p-5 pb-0 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary/90 flex items-center justify-center shrink-0 ring-2 ring-primary/50">
              <span className="text-lg sm:text-xl font-bold text-primary-foreground">
                {(displayName || '?').charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-display font-bold text-foreground truncate">{displayName}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">{t('publicProfile.publicProfile')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-muted/50 shrink-0 transition-colors" aria-label="Close">
            <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Info rows with icons */}
        <div className="px-4 sm:px-5 py-3 space-y-2">
          {locationText && (
            <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4 shrink-0 text-primary/80" />
              <span>{locationText}</span>
            </div>
          )}
          {joinedAtFormatted && (
            <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4 shrink-0 text-primary/80" />
              <span>{t('publicProfile.joinedOn', { date: joinedAtFormatted })}</span>
            </div>
          )}
        </div>

        {/* Stats pills */}
        {!loading && entry && (
          <div className="px-4 sm:px-5 pb-4 flex flex-wrap gap-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-muted/30 border border-muted/40">
              <Trophy className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium text-foreground">#{entry.rank}</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-muted/30 border border-muted/40">
              <Zap className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">{entry.totalPoints} {t('publicProfile.pts')}</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-muted/30 border border-muted/40">
              <Gamepad2 className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">{entry.predictionCount} {t('publicLeaderboard.games')}</span>
            </div>
          </div>
        )}

        {/* Results section */}
        <div className="flex-1 overflow-y-auto border-t border-muted/30 bg-muted/5">
          <div className="p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <ListOrdered className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">{t('publicProfile.results')}</h3>
            </div>
          {loading ? (
            <div className="text-center py-10">
              <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t('stats.loadingPredictions')}</p>
            </div>
          ) : sortedResults.length === 0 ? (
            <div className="text-center py-8 rounded-xl bg-muted/20 border border-muted/30">
              <Gamepad2 className="w-10 h-10 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{t('publicProfile.noResults')}</p>
              <p className="text-xs text-muted-foreground/80 mt-1">{t('publicProfile.noResultsHint')}</p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4 max-h-[45vh] overflow-y-auto">
              {sortedResults.slice(0, 20).map((item) => {
                const hasResult = item.match?.score != null && String(item.match.score).trim() !== ''
                const { winnerPoint, scorePoint } = computePointsSplit(item)
                const totalPt = winnerPoint + scorePoint
                const matchScoreStr = item.match?.score != null ? String(item.match.score).trim() : ''
                return (
                  <div key={item.prediction.id} className="glass-card rounded-lg sm:rounded-xl p-3 sm:p-4">
                    <div className="space-y-1.5 sm:space-y-2">
                      <div className="flex items-start justify-between gap-2 sm:gap-3">
                        <div className="flex items-center flex-wrap gap-2 min-w-0 flex-1">
                          <MatchTeamsInline
                            teamA={item.match.teamA}
                            teamB={item.match.teamB}
                            league={item.match.league}
                            separator="-"
                            nameClassName="font-semibold text-small sm:text-body text-foreground"
                          />
                          {hasResult && matchScoreStr !== '' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-primary/20 text-primary font-semibold text-xs sm:text-small border border-primary/30 shrink-0">
                              {t('roomPage.realScore')}: {matchScoreStr}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0 text-right whitespace-nowrap pt-0.5">{item.tournamentName}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">{formatDate(getMatchDate(item.match), true)}</div>
                      <div className="text-primary font-medium text-small sm:text-body">
                        {t('stats.playerScoreLabel')} {item.prediction.predictedScore}{buildWinnerSuffix(item.prediction.winner, t)}
                      </div>
                      {hasResult && (
                        <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-3 sm:gap-4 text-small sm:text-body">
                          <span className="text-muted-foreground">
                            {t('stats.resultLabel')}:{' '}
                            <span className="font-semibold text-foreground">{winnerPoint}</span>
                          </span>
                          <span className="text-muted-foreground">
                            {t('stats.scoreLabel')}:{' '}
                            <span className="font-semibold text-foreground">{scorePoint}</span>
                          </span>
                          <span
                            className={`ml-auto inline-flex items-center px-3 py-1 rounded-lg font-bold ${
                              totalPt === 0 ? 'bg-destructive/20 text-destructive' : 'bg-success/20 text-success'
                            }`}
                          >
                            {formatPoints(totalPt, t)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
              {sortedResults.length > 20 && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  {t('publicProfile.showingFirst', { count: String(20), total: String(sortedResults.length) })}
                </p>
              )}
            </div>
          )}
          </div>
        </div>

        <div className="p-4 sm:p-5 pt-3 border-t border-muted/30">
          <button
            onClick={onClose}
            className="w-full px-4 py-3 rounded-xl font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
          >
            {t('stats.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default PublicProfileModal
